import { LocalizedText, showSuccessAlert, SubmitHandler, useFormControls } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { useRepositoryMutation, UserProfileResult } from '@mews-commander/data-access';
import { useInvalidateUserProfile } from '../../hooks/useInvalidateUserProfile';
import { FieldNames } from '../FieldNames';
import { useEditUserFormProps } from './formProps';
import { getSubmittedUserEditValues } from './getSubmittedUserEditValues';
import { EditUserFormValues } from './schema';

export const useEditUserForm = ({
    userProfile,
    onSuccess,
}: {
    userProfile: UserProfileResult;
    onSuccess: () => void;
}) => {
    const { resolver, defaultValues, isFetchingData } = useEditUserFormProps(userProfile);
    const invalidateUserProfile = useInvalidateUserProfile();

    const editUserMutation = useRepositoryMutation('userProfiles', 'update', {
        onSuccess: () => {
            invalidateUserProfile(userProfile.userId);
            onSuccess();

            showSuccessAlert({
                description: (
                    <LocalizedText
                        textKey={TextKey.UserEditSavedDescription}
                        substitutes={{ name: userProfile.fullName || '' }}
                    />
                ),
            });
        },
    });

    const { getOptionsValue, getAdminValue, getRoleIdsValue, getPrivilegesValue, getLearningPathValue } =
        getSubmittedUserEditValues();

    const handleSubmit: SubmitHandler<EditUserFormValues> = (values, { dirtyFields }) => {
        const data = {
            editUserId: userProfile.userId,
            enterpriseIds: userProfile.enterprises?.map((enterprise) => enterprise.id),
            firstName: dirtyFields.firstName ? { value: values.firstName } : undefined,
            lastName: dirtyFields.lastName ? { value: values.lastName } : undefined,
            options: getOptionsValue(dirtyFields, values),
            isAdmin: getAdminValue(dirtyFields, values),
            roleIds: getRoleIdsValue(dirtyFields, values),
            privileges: getPrivilegesValue(dirtyFields, values),
            assignedLearningPath: getLearningPathValue(dirtyFields, values),
            departmentId: dirtyFields.departmentId ? { value: values[FieldNames.DepartmentId] } : undefined,
            superiorProfileId: dirtyFields.superiorProfileId
                ? { value: values[FieldNames.SuperiorProfileId] || null }
                : undefined,
            cashierIds: dirtyFields.cashiers ? { value: values[FieldNames.Cashiers] } : undefined,
            employeeIncentiveCode: dirtyFields.employeeIncentiveCode
                ? { value: values[FieldNames.EmployeeIncentiveCode] }
                : undefined,
        };

        void editUserMutation.mutate(data);
    };

    const otherMethods = useFormControls({
        onSubmit: handleSubmit,
        resolver,
        defaultValues,
    });

    return {
        ...otherMethods,
        isPending: editUserMutation.isPending || isFetchingData,
    };
};
